import { useMemo, useRef, useState, type ReactNode } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { StatusPill } from '@/components/status-pill'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuSearch,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import type { StatusTone } from '@/lib/status-tones'
import { cn } from '@/lib/utils'

export interface PickerChoice {
  value: string
  label: string
  tone?: StatusTone
  /** Shown in the menu instead of a pill or the plain label, e.g. a priority tag. */
  render?: ReactNode
}

/** Long lists get a search field, as the staff picker does. */
const SEARCH_THRESHOLD = 12

/**
 * Click a pill to pick a new value from a small menu that saves on selection (USA-1451). Used for
 * project and action-item priority and every status pill, so they all edit the same way as the
 * staff assignment picker. Safe inside row links: clicks never reach the row.
 */
export function ChoicePicker({
  title,
  label,
  value,
  choices,
  onSelect,
  pending = false,
  disabled = false,
  align = 'start',
  buttonTrigger = false,
  menuClassName,
  opensFollowUp = false,
  children,
}: {
  /** The menu's heading, e.g. "Project priority". */
  title: string
  /** Accessible name of the trigger, e.g. "Priority: High. Change priority". */
  label: string
  value: string
  choices: readonly PickerChoice[]
  onSelect: (value: string) => void
  pending?: boolean
  disabled?: boolean
  align?: 'start' | 'center' | 'end'
  /** ``children`` is itself a button (e.g. "Set PO status") and opens the menu as it is. */
  buttonTrigger?: boolean
  /** Widens the menu for choices that carry detail, e.g. "w-80". */
  menuClassName?: string
  /**
   * A pick opens a popover or sheet of its own (e.g. a reason to give): `onSelect` runs once the menu
   * has closed and released focus, and focus stays off the pill, so the new one isn't dismissed.
   */
  opensFollowUp?: boolean
  /** The pill showing the current value. */
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const searchable = choices.length > SEARCH_THRESHOLD
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    return query ? choices.filter((choice) => choice.label.toLowerCase().includes(query)) : choices
  }, [choices, search])

  const followUp = useRef<string | null>(null)
  const choose = (next: string) => {
    setOpen(false)
    setSearch('')
    if (next === value) return
    if (opensFollowUp) followUp.current = next
    else onSelect(next)
  }
  // Keep clicks on the trigger and the (portalled) menu from following a surrounding row link.
  const stop = (event: { preventDefault: () => void; stopPropagation: () => void }) => {
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <PickerMenu
      open={open}
      onOpenChange={(next) => {
        if (!pending) setOpen(next)
        if (!next) setSearch('')
      }}
    >
      <PickerMenuTrigger asChild disabled={disabled || pending}>
        {buttonTrigger ? (
          children
        ) : (
        <button
          type="button"
          aria-label={label}
          title={label}
          aria-busy={pending}
          onClick={stop}
          className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full transition-[filter,box-shadow] hover:brightness-95 hover:ring-2 hover:ring-ring/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-70 data-[state=open]:ring-2 data-[state=open]:ring-ring/40"
        >
          {children}
          {pending && <Loader2 className="size-3 animate-spin text-muted-foreground" aria-hidden />}
        </button>
        )}
      </PickerMenuTrigger>
      <PickerMenuContent
        title={title}
        align={align}
        className={cn('w-60', menuClassName)}
        onClick={(event) => event.stopPropagation()}
        onCloseAutoFocus={
          opensFollowUp
            ? (event) => {
                event.preventDefault()
                const next = followUp.current
                followUp.current = null
                if (next !== null) onSelect(next)
              }
            : undefined
        }
      >
        {searchable && (
          <PickerMenuSearch value={search} onChange={setSearch} ariaLabel={`Search ${title.toLowerCase()}`} />
        )}
        <PickerMenuList className={cn('max-h-96', !searchable && 'pt-0')}>
          {filtered.length ? (
            filtered.map((choice) => {
              const current = choice.value === value
              return (
                <PickerMenuItem
                  key={choice.value}
                  onSelect={() => choose(choice.value)}
                  aria-current={current || undefined}
                  className="min-h-9 justify-between"
                >
                  {choice.render ?? (choice.tone ? (
                    <StatusPill tone={choice.tone} className="text-xs">
                      {choice.label}
                    </StatusPill>
                  ) : (
                    <span className="truncate">{choice.label}</span>
                  ))}
                  {current && <Check className="size-4 shrink-0 text-primary" aria-label="Current" />}
                </PickerMenuItem>
              )
            })
          ) : (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">No matches</p>
          )}
        </PickerMenuList>
      </PickerMenuContent>
    </PickerMenu>
  )
}
